<template>
  <div class="Action-form Action-form--education">
    <div class="Action-form__input--education">
      <div v-if="isPositive" class="Action-form__input">
        <div
          class="Action-form__input__inputs  Action-form__input__inputs--education"
          v-for="(participant, index) in action.participants"
          :key="index"
        >
          <TextInput
            class="Form-input__item"
            label="Namn på utbildningsdeltagare"
            :disabled="isDisabled"
            @input="updateParticipantsData($event, 'name', index)"
            @blur="updateParticipants"
            type="text"
            placeholder="För- och efternamn..."
            :value="participant.name"
            :validation="$v.action.participants.$each[index].name"
          />
          <DatePickerInput
            class="Form-input__item"
            :value="
              participant.courseDate ? parseDate(participant.courseDate) : null
            "
            label="Datum för utbildning"
            element-id="date"
            :disabled="isDisabled"
            placeholder="Datum..."
            :disabledDates="disabledDates"
            type="date"
            @input="updateParticipantsData($event, 'courseDate', index)"
            @blur="updateParticipants"
            :removeText="!isDisabled && index > 0 ? 'Ta bort' : ''"
            @remove="removeEducation(index)"
            :custom-error-message="customErrorMessageOrgNo(index)"
            :validation="$v.action.participants.$each[index].courseDate"
          />
          <TextInput
            class="Form-input__item"
            label="Arbetsgivare vid utbildningstillfället"
            :disabled="isDisabled"
            @input="updateParticipantsData($event, 'companyName', index)"
            @blur="updateParticipants"
            type="text"
            placeholder="Företag..."
            :value="participant.companyName"
            :validation="$v.action.participants.$each[index].companyName"
          />
        </div>
      </div>
      <div
        class="Action-form__input Action-form__input__steps"
        v-else-if="isPositive === false"
      >
        <div class="Action-form__input__inputs">
          <div class="Form-input__item Form-input">
            <label class="Form-input__label Action-form__description"
              >1. Anmäl dig till utbildning</label
            >
            <a
              class="Action-form__link"
              target="_blank"
              href="https://www.kompetensforetagen.se/auktorisation-2/auktorisationsutbildning/"
              >https://www.kompetensforetagen.se</a
            >
          </div>
          <div class="Form-input__item Form-input">
            <label class="Form-input__label Action-form__description"
              >2. Bekräfta bokning</label
            >
            <CheckboxInput
              :disabled="isDisabled"
              text="Jag har gjort en anmälan"
              :checked="action.hasUpcomingEducation"
              @input="educationBooked"
            />
          </div>
        </div>
      </div>
      <div v-if="!isDisabled" class="Action-form__buttons">
        <div v-if="isPositive" class="Action-form__buttons__add">
          <button
            v-if="!isDisabled"
            class="Action-form__link"
            @click="addNewEducation"
          >
            + Lägg till deltagare
          </button>
        </div>
      </div>
      <div v-if="!isApplicant" class="Action-form__controls">
        <ToggleInput
          :value="approved"
          :disabled="!action.isValid || !isDisabled"
          text="Godkänt krav"
          @input="handleChangeApproved"
        />
        <div v-if="!approved">
          <button
            v-if="submitted"
            class="Button Button--secondary"
            @click="unlockAction"
          >
            Lås upp för komplettering
          </button>
          <button v-else class="Button Button--primary" @click="lockAction">
            Avsluta komplettering
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import TextInput from "@/components/forms/TextInput";
import DatePickerInput from "@/components/forms/DatePickerInput";
import CheckboxInput from "@/components/forms/CheckboxInput";
import ToggleInput from "@/components/forms/ToggleInput";
import { EventBus } from "@/services/event-bus";
import moment from "moment";
import _ from "lodash";
import { required } from "vuelidate/lib/validators";

const validDate = value => {
  const date = moment(value);
  return date.isValid();
};

export default {
  name: "ApplicationActionMetaEducation",
  components: { TextInput, CheckboxInput, DatePickerInput, ToggleInput },
  props: {
    action: {
      type: Object,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    isPositive: {
      type: Boolean,
      default: null
    }
  },
  data() {
    return {
      disabledDates: {
        from: new Date()
      }
    };
  },
  watch: {
    "action.participants": function() {
      EventBus.$emit("expander:resize");
    },
    "$v.$anyError": function() {
      EventBus.$emit("expander:resize");
    }
  },
  validations: {
    action: {
      participants: {
        $each: {
          courseDate: {
            validDate
          },
          companyName: {
            required
          },
          name: {
            required
          }
        }
      }
    }
  },
  mounted() {
    if (
      !this.approved &&
      !this.submitted &&
      this.action.actionIsPositive &&
      (!this.action.participants || this.action.participants.length === 0)
    ) {
      this.addNewEducation();
    }
  },
  computed: {
    ...mapGetters("memberStore", ["isApplicant"]),
    ...mapState("applicationStore", ["application"]),
    approved() {
      return this.action.status === "APPROVED";
    },
    submitted() {
      return this.action.status === "IN_REVIEW";
    },
    isDisabled() {
      return this.submitted || this.approved;
    },
    customErrorMessageOrgNo(_) {
      return i => {
        const el = this.$v.action.participants.$each[i].courseDate;
        if (!el.validDate) {
          return "Ogiltigt datum";
        }
        return null;
      };
    }
  },
  methods: {
    ...mapActions("applicationStore", [
      "approveAction",
      "updateEducation",
      "submitAction",
      "updateHasUpcomingEducation"
    ]),
    updateEducationAction({
      save = true,
      participants = this.action.participants,
      actionIsPositive = true,
      hasUpcomingEducation = this.action.hasUpcomingEducation
    }) {
      this.updateEducation({
        actionId: this.action.id,
        save: save,
        data: {
          actionIsPositive: actionIsPositive,
          hasUpcomingEducation: hasUpcomingEducation,
          participants: participants
        }
      });
    },
    updateParticipantsData(value, field, index) {
      const participants = [...this.action.participants];
      const dateUpdate = field === "courseDate";
      if (dateUpdate) {
        const date = moment(value);
        value = date.isValid() ? date.format("YYYY-MM-DD") : null;
      }
      participants[index][field] = value;
      this.updateEducationAction({
        save: dateUpdate,
        participants: participants
      });
    },
    updateParticipants() {
      this.updateEducationAction({});
    },
    addNewEducation() {
      const participants = [...this.action.participants];
      const max = _.maxBy(participants, function(o) {
        return o.sortIndex;
      });
      participants.push({
        name: "",
        companyName: "",
        courseDate: "",
        sortIndex: max ? max.sortIndex + 1 : 1
      });
      this.updateEducationAction({ save: false, participants: participants });
    },
    parseDate(val) {
      const date = moment(val);
      return val && date.isValid() ? date.toDate() : null;
    },
    removeEducation(index) {
      const participants = [...this.action.participants];
      participants.splice(index, 1);

      this.updateEducationAction({ participants: participants });
    },
    educationBooked(isChecked) {
      this.updateEducationAction({
        actionIsPositive: false,
        participants: this.action.participants,
        hasUpcomingEducation: isChecked
      });
    },
    handleChangeApproved(isChecked) {
      this.approveAction({
        actionId: this.action.id,
        approved: isChecked
      });
    },
    unlockAction() {
      this.submitAction({
        actionId: this.action.id,
        submitted: false
      });
    },
    lockAction() {
      this.submitAction({
        actionId: this.action.id,
        submitted: true
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.Button--primary-inactive {
  opacity: 0.7;
  color: #fff;
}
</style>
